export const lessOpinionatedCurrencyFormatter = (
  value: number,
  storeCurrency: { code: string; locale: string }
): string => {
  try {
    // First format with currency symbol
    const formattedWithSymbol = new Intl.NumberFormat(storeCurrency.locale, {
      style: "currency",
      currency: storeCurrency.code,
    })
      .format(value)
      .replace(/\D00(?=\D*$)/, "");

    return formattedWithSymbol;
  } catch (e) {
    // Invalid locale, fallback to USD
    const fallbackFormatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: storeCurrency.code,
    })
      .format(value)
      .replace(/\D00(?=\D*$)/, "");

    return fallbackFormatted;
  }
};

export const percentFormatter = (
  value: number,
  tick: boolean,
  storeCurrency: { code: string; locale: string }
): string => {
  let formatted = `${new Intl.NumberFormat(`${storeCurrency.locale}`, {
    style: "percent",
    notation: tick ? "compact" : undefined,
    maximumSignificantDigits: tick ? 3 : undefined,
    minimumSignificantDigits: tick ? 1 : undefined,
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  }).format(value)}`;
  if (formatted === "0.00%" || formatted === "0.0%" || formatted === "100.0%") {
    formatted = formatted.replace(/\.0+/, "");
  }
  return formatted;
};

export const BFCMPercentFormatter = (
  value: number,
  tick: boolean,
  storeCurrency: { code: string; locale: string }
): string => {
  let formatted = `${new Intl.NumberFormat(`${storeCurrency.locale}`, {
    style: "percent",
    notation: tick ? "compact" : undefined,
    maximumSignificantDigits: tick ? 3 : undefined,
    minimumSignificantDigits: tick ? 1 : undefined,
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  }).format(value)}`;
  if (formatted === "0.00%" || formatted === "0.0%" || formatted === "100.0%") {
    formatted = formatted.replace(/\.0+/, "");
  }
  return formatted;
};

export const percentNoDecimalFormatter = (
  value: number,
  tick: boolean,
  storeCurrency: { code: string; locale: string }
): string => {
  return `${new Intl.NumberFormat(`${storeCurrency.locale}`, {
    style: "percent",
    notation: tick ? "compact" : undefined,
    maximumSignificantDigits: tick ? 3 : undefined,
    minimumSignificantDigits: tick ? 1 : undefined,
    maximumFractionDigits: 0,
    minimumFractionDigits: undefined,
  }).format(value)}`;
};

// enforces 2 decimal places
export const percentTwoDecimalFormatter = (
  value: number,
  tick: boolean,
  storeCurrency: { code: string; locale: string },
  decimalAmountShown?: number
): string => {
  let formatted = `${new Intl.NumberFormat(`${storeCurrency.locale}`, {
    style: "percent",
    notation: tick ? "compact" : undefined,
    maximumFractionDigits: decimalAmountShown || 1,
    minimumFractionDigits: decimalAmountShown || 1,
  }).format(value)}`;
  if (formatted === "0.00%" || formatted === "0.0%" || formatted === "100.0%") {
    formatted = formatted.replace(/\.0+/, "");
  }
  return formatted;
};

export const numberFormatter = (
  value: number,
  tick: boolean,
  storeCurrency: { code: string; locale: string },
  maximumFractionDigits?: number,
  showDecimals: boolean = true,
  maximumSignificantDigits?: number
): string => {
  return `${new Intl.NumberFormat(`${storeCurrency.locale}`, {
    style: "decimal",
    notation: tick ? "compact" : undefined,
    compactDisplay: tick ? "short" : undefined,

    // Use EITHER significant digits OR fraction digits based on conditions
    ...(tick
      ? {
          // For tick=true (compact notation), use significant digits
          maximumSignificantDigits: maximumSignificantDigits ?? 2,
          minimumSignificantDigits: 1,
        }
      : {
          // For tick=false (normal notation), use fraction digits only
          minimumFractionDigits: showDecimals ? 2 : 0,
          maximumFractionDigits: showDecimals
            ? (maximumFractionDigits ?? 2)
            : 0,
        }),
  }).format(value)}`;
};

export const numberNoRoundingFormatter = (
  value: number,
  tick: boolean,
  storeCurrency: { code: string; locale: string },
  maximumSignificantDigits?: number
): string => {
  return `${new Intl.NumberFormat(`${storeCurrency.locale}`, {
    style: "decimal",
    notation: tick ? "compact" : undefined,
    compactDisplay: tick ? "short" : undefined,
    maximumSignificantDigits,
  }).format(value)}`;
};

export const noDecimalCurrencyFormatter = (
  value: number,
  tick: boolean,
  storeCurrency: { code: string; locale: string },
  maximumSignificantDigits?: number
): string => {
  return `${new Intl.NumberFormat(`${storeCurrency.locale}`, {
    style: "currency",
    currency: `${storeCurrency.code}`,
    notation: tick ? "compact" : undefined,
    compactDisplay: tick ? "short" : undefined,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    maximumSignificantDigits,
  })
    .format(value)
    .replace(/\D00(?=\D*$)/, "")}`;
};

export const noDecimalNumberFormatter = (
  value: number,
  tick: boolean,
  storeCurrency: { code: string; locale: string },
  maximumSignificantDigits?: number
): string => {
  return `${new Intl.NumberFormat(`${storeCurrency.locale}`, {
    style: "decimal",
    notation: tick ? "compact" : undefined,
    compactDisplay: tick ? "short" : undefined,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
    maximumSignificantDigits,
  }).format(value)}`;
};

export const noPrefixCurrencyFormatter = (
  value: number,
  storeCurrency: { code: string; locale: string }
) => {
  return new Intl.NumberFormat(`${storeCurrency.locale}`, {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(value)
    .replace(/\D00(?=\D*$)/, "");
};

export const currencyFormatter = (
  value: number,
  tick: boolean,
  storeCurrency: { code: string; locale: string },
  showDecimals: boolean = true,
  shouldShorten: boolean = false,
  maximumSignificantDigits?: number
): string => {
  try {
    // First format with currency symbol
    const formattedWithSymbol = new Intl.NumberFormat(storeCurrency.locale, {
      style: "currency",
      currency: storeCurrency.code,
      notation: tick ? "compact" : undefined,
      compactDisplay: tick ? "short" : undefined,
      minimumFractionDigits: showDecimals ? 2 : 0,
      maximumFractionDigits: showDecimals ? 2 : 0,
      maximumSignificantDigits,
    })
      .format(value)
      .replace(/\D00(?=\D*$)/, showDecimals ? "$&" : "");

    // Check if shouldShorten is true and the formatted string exceeds 5 characters
    if (shouldShorten && formattedWithSymbol.length > 5) {
      // Format without currency symbol
      const formattedWithoutSymbol = new Intl.NumberFormat(
        storeCurrency.locale,
        {
          style: "decimal",
          notation: tick ? "compact" : undefined,
          compactDisplay: tick ? "short" : undefined,
          minimumFractionDigits: showDecimals ? 2 : 0,
          maximumFractionDigits: showDecimals ? 2 : 0,
        }
      )
        .format(value)
        .replace(/\D00(?=\D*$)/, showDecimals ? "$&" : "");

      return formattedWithoutSymbol;
    }

    return formattedWithSymbol;
  } catch (e) {
    // Invalid locale, fallback to USD
    const fallbackFormatted = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: storeCurrency.code,
      notation: tick ? "compact" : undefined,
      compactDisplay: tick ? "short" : undefined,
      minimumFractionDigits: showDecimals ? 2 : 0,
      maximumFractionDigits: showDecimals ? 2 : 0,
    })
      .format(value)
      .replace(/\D00(?=\D*$)/, showDecimals ? "$&" : "");

    // Check if shouldShorten is true and the fallback formatted string exceeds 5 characters
    if (shouldShorten && fallbackFormatted.length > 5) {
      // Format without currency symbol
      return new Intl.NumberFormat("en-US", {
        style: "decimal",
        notation: tick ? "compact" : undefined,
        compactDisplay: tick ? "short" : undefined,
        minimumFractionDigits: showDecimals ? 2 : 0,
        maximumFractionDigits: showDecimals ? 2 : 0,
      })
        .format(value)
        .replace(/\D00(?=\D*$)/, showDecimals ? "$&" : "");
    }

    return fallbackFormatted;
  }
};
